import { PORTAL_OPEN_MS, PORTAL_CLOSE_MS, RING_CONIC } from './callModel';

/**
 * The call's portal (handoff Part B §4): the call screen opens OUT of a ring at whatever
 * was tapped and closes back INTO it. Same idea as the messages doorway (PortalDoorway /
 * portalKeyframes), on a fixed layer: the layer is clipped `circle(20px at origin)` →
 * `circle(R at origin)` where R reaches the farthest corner; in sync a 100px conic ring,
 * masked to a 6px band, scales from 0.44 to R/47 and fades out at the end of the open. The
 * close runs it in reverse and lands on the origin. Reduced motion: no animation.
 */
export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function coverRadiusFor(origin, box) {
  return Math.hypot(Math.max(origin.x, box.width - origin.x), Math.max(origin.y, box.height - origin.y)) + 4;
}

/** Style for the ring element the layer draws (positioned by the animation). */
export const ringStyle = {
  position: 'absolute', left: 0, top: 0, width: 100, height: 100, borderRadius: '50%', zIndex: 60, pointerEvents: 'none', opacity: 0,
  background: RING_CONIC,
  WebkitMask: 'radial-gradient(circle, transparent 43px, #000 44px)',
  mask: 'radial-gradient(circle, transparent 43px, #000 44px)',
};

/**
 * Run the portal on `layer` (the fixed call layer) and `ring` (its ring element).
 * `origin` is in the layer's own coordinates; null means the centre (an incoming call).
 * Resolves when the clip has finished.
 */
export function runCallPortal({ layer, ring, origin = null, open = true }) {
  return new Promise((resolve) => {
    if (!layer || !layer.animate || reducedMotion()) { resolve(); return; }
    const box = layer.getBoundingClientRect();
    const o = origin || { x: box.width / 2, y: box.height * 0.4 };
    const R = coverRadiusFor(o, box);
    const small = `circle(20px at ${o.x}px ${o.y}px)`;
    const big = `circle(${R}px at ${o.x}px ${o.y}px)`;
    const ms = open ? PORTAL_OPEN_MS : PORTAL_CLOSE_MS;
    const easing = open ? 'cubic-bezier(.2,.7,.2,1)' : 'cubic-bezier(.5,0,.75,0)';
    const a = layer.animate({ clipPath: open ? [small, big] : [big, small] }, { duration: ms, easing, fill: 'forwards' });
    a.onfinish = () => resolve();
    a.oncancel = () => resolve();
    if (ring && ring.animate) {
      ring.style.left = `${o.x - 50}px`;
      ring.style.top = `${o.y - 50}px`;
      const s0 = 0.44;
      const s1 = R / 47;
      ring.animate(open
        ? [{ transform: `scale(${s0})`, opacity: 1 }, { transform: `scale(${s1 * 0.6})`, opacity: 0.9, offset: 0.6 }, { transform: `scale(${s1})`, opacity: 0 }]
        : [{ transform: `scale(${s1})`, opacity: 0 }, { transform: `scale(${s1 * 0.4})`, opacity: 0.9, offset: 0.5 }, { transform: `scale(${s0})`, opacity: 1, offset: 0.92 }, { transform: `scale(${s0 * 0.6})`, opacity: 0 }],
        { duration: open ? ms : ms + 120, easing: 'linear' });
    }
  });
}
